import { useEffect } from 'react';

const useIframeScrollWindowMessage = (ref: React.RefObject<HTMLElement>) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      const atTop = el.scrollTop <= 0 && e.deltaY < 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && e.deltaY > 0;
      if (atTop || atBottom) {
        e.preventDefault();
        window.parent.postMessage({ type: 'scroll', deltaY: e.deltaY }, '*');
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });

    if (!('ontouchstart' in window) || window === window.parent) {
      return () => el.removeEventListener('wheel', handleWheel);
    }

    let lastY = 0;
    let velocity = 0;
    let rafId: number | null = null;

    const handleTouchStart = (e: TouchEvent) => {
      lastY = e.touches[0].clientY;
      velocity = 0;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touchY = e.touches[0].clientY;
      const deltaY = lastY - touchY;
      lastY = touchY;
      velocity = deltaY;

      const atTop = el.scrollTop <= 0 && deltaY < 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && deltaY > 0;

      if (atTop || atBottom) {
        e.preventDefault();
        window.parent.postMessage({ type: 'scroll', deltaY }, '*');
      }
    };

    const handleTouchEnd = () => {
      const atBoundary =
        (el.scrollTop <= 0 && velocity < 0) ||
        (el.scrollTop + el.clientHeight >= el.scrollHeight - 1 && velocity > 0);

      if (!atBoundary) return;

      const decelerate = () => {
        if (Math.abs(velocity) < 0.5) return;
        velocity *= 0.92;
        window.parent.postMessage({ type: 'scroll', deltaY: velocity }, '*');
        rafId = requestAnimationFrame(decelerate);
      };
      decelerate();
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd);

    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [ref]);
};

export default useIframeScrollWindowMessage;
